/**
 * Shared content-type detection logic used by both UserUploadModal and AdminAddWallpaper.
 *
 * "ContentType" here is the UI-facing concept:
 *   wallpaper  → static image (JPG / PNG / WEBP)
 *   live       → video (GIF / MP4 / MOV)
 *   ringtone   → audio (MP3 / WAV / OGG / AAC / M4A)
 *
 * The DB field `mediaType` uses slightly different strings — see MEDIA_TYPE_MAP below.
 */

export type ContentType = 'wallpaper' | 'live' | 'ringtone';

// DB-level mediaType strings
export const MEDIA_TYPE_MAP: Record<ContentType, string> = {
  wallpaper: 'wallpaper',
  live: 'live-wallpaper',
  ringtone: 'ringtone',
};

// Human-readable badge for each detected type
export const CONTENT_TYPE_BADGE: Record<ContentType, { emoji: string; label: string }> = {
  wallpaper: { emoji: '📷', label: 'Wallpaper' },
  live: { emoji: '🎬', label: 'Live' },
  ringtone: { emoji: '🎵', label: 'Ringtone' },
};

// The accept attribute for the file input — all supported formats
export const ALL_ACCEPT_ATTR =
  'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac,audio/x-m4a';

// Human-readable format hint shown below the file picker
export const ALL_FORMAT_HINT =
  'JPG, PNG, WEBP → Wallpaper · GIF, MP4, MOV → Live · MP3, WAV, OGG, AAC, M4A → Ringtone';

const EXT_TO_CONTENT_TYPE: Record<string, ContentType> = {
  jpg: 'wallpaper',
  jpeg: 'wallpaper',
  png: 'wallpaper',
  webp: 'wallpaper',
  gif: 'live',
  mp4: 'live',
  mov: 'live',
  mp3: 'ringtone',
  wav: 'ringtone',
  ogg: 'ringtone',
  aac: 'ringtone',
  m4a: 'ringtone',
};

const MIME_TO_CONTENT_TYPE: Record<string, ContentType> = {
  'image/jpeg': 'wallpaper',
  'image/png': 'wallpaper',
  'image/webp': 'wallpaper',
  'image/gif': 'live',
  'video/mp4': 'live',
  'video/quicktime': 'live',
  'video/webm': 'live',
  'audio/mpeg': 'ringtone',
  'audio/mp3': 'ringtone',
  'audio/wav': 'ringtone',
  'audio/ogg': 'ringtone',
  'audio/aac': 'ringtone',
  'audio/x-m4a': 'ringtone',
  'audio/mp4': 'ringtone',
};

/**
 * Detect content type from a File object. Returns null if the file
 * type is not recognized (caller should reject the file).
 */
export function detectContentType(file: File): ContentType | null {
  // First try MIME type (most reliable)
  const mime = file.type.toLowerCase();
  if (mime && MIME_TO_CONTENT_TYPE[mime]) return MIME_TO_CONTENT_TYPE[mime];

  // Fallback to file extension
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext && EXT_TO_CONTENT_TYPE[ext]) return EXT_TO_CONTENT_TYPE[ext];

  return null;
}

// Base fallback categories for each content type.
// NOTE: The authoritative source is siteSettings/wallpaperCategories in the DB.
// Use useWallpaperCategoriesFromDB() from @/utils/wallpaperCategories for live reads.
// This constant is kept for backward compatibility and as the offline fallback.
export { BASE_WALLPAPER_CATEGORIES } from '@/utils/wallpaperCategories';
export const BASE_LIVE_CATEGORIES = [
  'Cinematic', 'Loop', 'Nature', 'Abstract', 'Cyberpunk', 'Minimal',
];
export const RINGTONE_CATEGORIES = [
  'Pop', 'Hip-Hop', 'Electronic', 'Rock', 'Ambient', 'Soundtrack', 'Lo-Fi', 'Nature Sounds',
] as const;
export type RingtoneCategory = typeof RINGTONE_CATEGORIES[number];
